import type { FieldError, FieldErrors, FieldValues } from 'react-hook-form';

type ScrollToFirstErrorInFormOptions = {
  root?: ParentNode;
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
};

type FieldErrorsMap = {
  [fieldName: string]: FieldError | FieldErrorsMap;
};

const defaultScrollOptions: ScrollIntoViewOptions = {
  behavior: 'smooth',
  block: 'center',
};

const errorElementSelector = [
  '[aria-invalid="true"]',
  '.Mui-error',
  '[data-has-error="true"]',
  '[data-test$="-error"]',
].join(',');

const isFieldErrorsMap = (value: unknown): value is FieldErrorsMap => {
  return typeof value === 'object' && value !== null;
};

const isFieldError = (value: FieldError | FieldErrorsMap): value is FieldError => {
  const possibleError = value as Partial<FieldError>;
  return typeof possibleError.type === 'string' || typeof possibleError.message === 'string';
};

const isVisibleElement = (element: HTMLElement): boolean => {
  if (element.getAttribute('type') === 'hidden' || element.closest('[hidden], [aria-hidden="true"]')) {
    return false;
  }

  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }

  return element.getClientRects().length > 0;
};

const getFirstErrorPath = (errors: FieldErrorsMap, parentPath = ''): string | undefined => {
  for (const [fieldName, fieldError] of Object.entries(errors)) {
    const fieldPath = parentPath ? `${parentPath}.${fieldName}` : fieldName;

    if (isFieldError(fieldError)) {
      return fieldPath;
    }

    const nestedFieldPath = getFirstErrorPath(fieldError, fieldPath);

    if (nestedFieldPath) {
      return nestedFieldPath;
    }
  }

  return undefined;
};

const getFormFieldByName = (fieldName: string, root: ParentNode): HTMLElement | undefined => {
  const fields = Array.from(document.getElementsByName(fieldName));
  const field = fields.find(field => root === document || root.contains(field));

  return field instanceof HTMLElement ? field : undefined;
};

const getFirstErrorElement = (root: ParentNode): HTMLElement | undefined => {
  const fields = Array.from(root.querySelectorAll(errorElementSelector));

  const field = fields.find(field => field instanceof HTMLElement && isVisibleElement(field));

  if (!(field instanceof HTMLElement)) {
    return undefined;
  }

  const fieldContainer = field.closest('.MuiFormControl-root, [data-test]');

  return fieldContainer instanceof HTMLElement && isVisibleElement(fieldContainer) ? fieldContainer : field;
};

const scrollToElement = (
  element: HTMLElement,
  { behavior, block }: Pick<ScrollToFirstErrorInFormOptions, 'behavior' | 'block'>
): void => {
  element.scrollIntoView({
    ...defaultScrollOptions,
    behavior: behavior ?? defaultScrollOptions.behavior,
    block: block ?? defaultScrollOptions.block,
  });

  element.focus({ preventScroll: true });
};

export const scrollToFirstErrorInForm = <T extends FieldValues>(
  errors: FieldErrors<T>,
  options: ScrollToFirstErrorInFormOptions = {}
): boolean => {
  if (!isFieldErrorsMap(errors)) {
    return false;
  }

  const { root = document, behavior, block } = options;
  const fieldName = getFirstErrorPath(errors);
  const fieldByName = fieldName ? getFormFieldByName(fieldName, root) : undefined;
  const field = fieldByName && isVisibleElement(fieldByName) ? fieldByName : getFirstErrorElement(root);

  if (!field) {
    return false;
  }

  scrollToElement(field, {
    behavior,
    block,
  });

  return true;
};

export const scrollToFirstVisibleErrorInForm = (options: ScrollToFirstErrorInFormOptions = {}): boolean => {
  const { root = document, behavior, block } = options;
  const field = getFirstErrorElement(root);

  if (!field) {
    return false;
  }

  scrollToElement(field, {
    behavior,
    block,
  });

  return true;
};
